import { Box, Stack, styled } from '@mui/material'
import { ReactNode } from 'react'
import { Typography, Icon, Colors, InfoTooltip, LoadingSkeleton } from 'ComponentLibrary'
import { Check } from 'react-feather'
import palette from 'ComponentLibrary/src/styles/colors'

export type FactSelectionBoxProps = {
    title: string
    subtitle: string
    infoText?: string
    isSelected: boolean
    children?: ReactNode
    onClick?: () => void
    isFactsPending?: boolean
    hideBorder?: boolean
    disabled?: boolean
}

export function FactSelectionBox({
    title,
    subtitle,
    infoText,
    isSelected,
    children,
    onClick,
    isFactsPending = false,
    hideBorder,
    disabled,
}: FactSelectionBoxProps) {
    if (isFactsPending) {
        return <LoadingSkeleton height="110px" />
    }

    return (
        <StyledSelectionBox $highlighted={isSelected} $hideBorder={hideBorder} $disabled={disabled} onClick={onClick}>
            <Stack direction="row" spacing={4}>
                <Stack direction="column" justifyContent="center" width={1} spacing={1} height={40}>
                    <Stack direction="row" alignItems="center" spacing={2}>
                        <StyledTypography variant="headline4" color={disabled ? palette.customtext.black38 : undefined}>
                            {title}
                        </StyledTypography>
                        {isSelected && <Icon icon={Check} color={Colors.success.main} size={16} style={{ minWidth: '16px' }} />}
                    </Stack>
                    <Typography variant="subtitle1">{subtitle}</Typography>
                </Stack>
                <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={2}>
                    {children}
                    {infoText && <InfoTooltip text={infoText} />}
                </Stack>
            </Stack>
        </StyledSelectionBox>
    )
}

const StyledTypography = styled(Typography)`
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 2; /* number of lines to show */
    line-clamp: 2;
    -webkit-box-orient: vertical;
`

const StyledSelectionBox = styled(Box, {
    shouldForwardProp: (prop: string) => prop[0] !== '$',
})<{ $highlighted: boolean; $hideBorder?: boolean; $disabled?: boolean }>(({ theme, $highlighted, $hideBorder, $disabled }) => ({
    maxWidth: '580px',
    height: '40px',
    padding: '17px 24px',
    borderRadius: '12px',
    border: !$hideBorder ? `1.5px solid ${$highlighted ? theme.palette.primary1.blue100 : theme.palette.customtext.black23}` : '',
    boxSizing: 'content-box',
    '&:hover': !$hideBorder &&
        !$disabled && {
            cursor: 'pointer',
            borderColor: theme.palette.primary1.blue100,
        },
}))
